import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledDataGrid } from '../UserRequestsStyles';
import { buildRegistrationColumns } from './columns';
import { ToolbarPropsOverrides } from '@mui/x-data-grid';
import WhitelistDrawer from './WhitelistDrawer/WhitelistDrawer';
import UserRequestsToolbar from '../UserRequestsToolbar';
import { Button } from '@material-ui/core';
import { useUserRequestsPermissions } from '../hooks';
import {
  useRegistration,
  useUpdateUserRequestStatus,
} from '../hooks/useRegistration';
import { RegistrationRequestStatusEnum } from '../types';
import { usePagination } from '../../../shared/hooks/usePagination';
import { useUserDateFormatter } from '../hooks';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../shared/api/getApiErrorMessage';

const RegistrationRequests = () => {
  const { mutateAsync: updateUserRequestStatus } = useUpdateUserRequestStatus();
  const { formatDateTime: formatDate } = useUserDateFormatter();
  const { enqueueSnackbar } = useSnackbar();

  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    filters,
    sortField,
    sortOrder,
    handleFilterModelChange,
    handlePagination,
    handleSortModelChange,
  } = usePagination({
    initialPageSize: 10,
    initialSortOrder: 'asc',
  });

  const { permissions } = useUserRequestsPermissions();
  const canWriteRegistration = !!permissions.registration.write;
  const canWriteWhitelist = !!permissions.whitelist.write;
  const isWhitelistEnabled = !!permissions.whitelist.read || canWriteWhitelist;

  const {
    data: registrationRequests,
    isLoading,
    isFetching,
    isError,
    error,
  } = useRegistration({
    limit: pageSize,
    page: pageIndex,
    filters,
    sortField,
    sortOrder,
  });

  const rows = useMemo(
    () => registrationRequests?.elements || [],
    [registrationRequests],
  );
  const totalRows = registrationRequests?.total || 0;
  const [isWhitelistOpen, setIsWhitelistOpen] = useState(false);

  useEffect(() => {
    if (!isError) return;

    enqueueSnackbar(
      getApiErrorMessage(error, 'Failed to load registration requests'),
      {
        variant: 'error',
      },
    );
  }, [enqueueSnackbar, error, isError]);

  const handleApprove = async (id: string) => {
    try {
      await updateUserRequestStatus({
        userId: id,
        status: RegistrationRequestStatusEnum.APPROVED,
      });
    } catch (mutationError) {
      enqueueSnackbar(
        getApiErrorMessage(mutationError, 'Failed to approve request'),
        {
          variant: 'error',
        },
      );
    }
  };
  const handleDecline = async (id: string) => {
    try {
      await updateUserRequestStatus({
        userId: id,
        status: RegistrationRequestStatusEnum.DECLINED,
      });
    } catch (mutationError) {
      enqueueSnackbar(
        getApiErrorMessage(mutationError, 'Failed to decline request'),
        {
          variant: 'error',
        },
      );
    }
  };

  const renderToolbar = useCallback(
    (props: ToolbarPropsOverrides) => (
      <UserRequestsToolbar
        {...props}
        total={totalRows}
        label="Registration Requests"
        actions={
          isWhitelistEnabled && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsWhitelistOpen(true)}
            >
              Manage Whitelist
            </Button>
          )
        }
      />
    ),
    [totalRows, isWhitelistEnabled],
  );

  return (
    <>
      <StyledDataGrid
        rows={rows}
        columns={buildRegistrationColumns(
          handleApprove,
          handleDecline,
          canWriteRegistration,
          formatDate,
        )}
        loading={isLoading || isFetching}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        filterMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        rowCount={totalRows}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        slots={{ toolbar: renderToolbar }}
        showToolbar
        disableRowSelectionOnClick
      />
      <WhitelistDrawer
        isOpen={isWhitelistOpen}
        onClose={() => setIsWhitelistOpen(false)}
        readOnly={!canWriteWhitelist}
      />
    </>
  );
};

export default RegistrationRequests;
